import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CheckIcon, GiftIcon, Loader2Icon, LockIcon, ShoppingBagIcon } from "lucide-react";
import { CartLine } from "../components/CartLine";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { ProductGrid } from "../components/ProductGrid";
import { SectionHeader } from "../components/SectionHeader";
import { useCart } from "../contexts/CartContext";
import { newArrivalIds, products } from "../data/products";
import { formatPrice } from "../utils/format";
import { btnPrimary, container, ease } from "../utils/styles";
export function Cart() {
  const {
    items,
    subtotal,
    count,
    clear
  } = useCart();
  const [gift, setGift] = useState(false);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "done">("idle");
  const inCart = new Set(items.map(i => i.product.id));
  const suggestions = newArrivalIds.map(id => products.find(p => p.id === id)!).filter(p => !inCart.has(p.id)).slice(0, 4);
  const checkout = () => {
    setStatus("loading");
    window.setTimeout(() => {
      setStatus("done");
      clear();
    }, 1000);
  };
  if (status === "done") {
    return <section className="flex flex-col items-center px-5 py-32 text-center" role="status" data-id="element-453" data-mp="24jyc62hzi0np">
        <motion.span initial={{
        scale: 0.8,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 0.25,
        ease
      }} className="flex h-16 w-16 items-center justify-center rounded-full bg-ink text-paper" data-id="element-454" data-mp="2sa261i5dem1i">
          <CheckIcon className="h-7 w-7" data-id="element-455" data-mp="2j9vwg7csm67m" />
        </motion.span>
        <h1 className="mt-6 text-3xl font-medium tracking-[-0.03em] text-ink" data-id="element-456" data-mp="2jbgu45uem59p">Doorgestuurd naar de kassa</h1>
        <p className="mt-3 text-muted" data-id="element-457" data-mp="17tyy5sj2e9xq">In de live winkel ga je verder naar de beveiligde Shopify checkout.</p>
        <Link to="/shop" onClick={() => setStatus("idle")} className={`${btnPrimary} mt-8`} data-id="element-458" data-mp="1bvalfreguj2r">
          Verder winkelen
        </Link>
      </section>;
  }
  return <>
      <section className={`${container} pb-16 pt-6 lg:pb-24`} data-id="element-459" data-mp="3ek2aqteog1yf">
        <Breadcrumbs items={[{
        label: "Home",
        to: "/"
      }, {
        label: "Winkelwagen"
      }]} data-id="element-460" data-mp="39jmu9sz3mec8" />
        <h1 className="mt-6 text-4xl font-medium tracking-[-0.04em] text-ink sm:text-5xl" data-id="element-461" data-mp="34r3j12nvvval">
          Winkelwagen <span className="text-muted" data-id="element-462" data-mp="2zsjvwyxk0dgn">({count})</span>
        </h1>

        {items.length === 0 ? <div className="mt-8 flex flex-col items-center rounded-xl bg-sand px-6 py-24 text-center" data-id="element-463" data-mp="2vshv0rzn7o2f">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-paper" data-id="element-464" data-mp="2qr26ur2651ft">
              <ShoppingBagIcon className="h-6 w-6 text-muted" strokeWidth={1.6} data-id="element-465" data-mp="2lu2bba2y3h2y" />
            </span>
            <p className="mt-5 text-xl font-medium text-ink" data-id="element-466" data-mp="2gw0g5qs8vrb4">Je winkelwagen is nog leeg</p>
            <Link to="/shop" className={`${btnPrimary} mt-7`} data-id="element-467" data-mp="k0w1ba60ix10">
              Naar de shop
            </Link>
          </div> : <div className="mt-8 grid gap-8 lg:grid-cols-12 lg:gap-14" data-id="element-468" data-mp="f19mwxeg7aqm">
            <ul className="divide-y divide-line border-y border-line lg:col-span-7" data-id="element-469" data-mp="njlbzveark1t">
              <AnimatePresence initial={false} data-id="element-470" data-mp="rkdmerywnuzo">
                {items.map(item => <CartLine key={item.key} item={item} data-id="element-471" data-mp="wdg6m9hsls37" />)}
              </AnimatePresence>
            </ul>

            <aside aria-label="Overzicht" className="lg:col-span-5" data-id="element-472" data-mp="11azyic7ey3dm">
              <div className="rounded-xl bg-sand p-6 sm:p-8 lg:sticky lg:top-[96px]" data-id="element-473" data-mp="4m0k0fg8k5cz">
                <h2 className="mb-5 text-lg font-medium text-ink" data-id="element-474" data-mp="8maspzvex9vj">Overzicht</h2>
                <dl className="space-y-3 text-[15px]" data-id="element-475" data-mp="dlx6caf5ks4v">
                  <div className="flex justify-between" data-id="element-476" data-mp="dkcbsksu9970">
                    <dt className="text-muted" data-id="element-477" data-mp="iljishn5q4cm">Subtotaal</dt>
                    <dd className="text-ink" data-id="element-478" data-mp="iixcsmfwzwwi">{formatPrice(subtotal)}</dd>
                  </div>
                  <div className="flex justify-between" data-id="element-479" data-mp="1p36o8simvc42">
                    <dt className="text-muted" data-id="element-480" data-mp="1tz43swm2oefs">Verzending</dt>
                    <dd className="text-muted" data-id="element-481" data-mp="32x3zcqmzmgqv">Bij afrekenen</dd>
                  </div>
                  <div className="flex justify-between border-t border-ink/10 pt-4 text-2xl font-medium tracking-[-0.02em]" data-id="element-482" data-mp="32yyjw3uck6uf">
                    <dt className="text-ink" data-id="element-483" data-mp="3bybj83orbf1f">Totaal</dt>
                    <dd className="text-ink" data-id="element-484" data-mp="3bz3h5l5ttouv">{formatPrice(subtotal)}</dd>
                  </div>
                </dl>

                <label className="mt-6 flex cursor-pointer items-center gap-3 rounded-lg bg-paper px-4 py-3 text-sm text-ink" data-id="element-485" data-mp="3711vqtkxhdg8">
                  <input type="checkbox" checked={gift} onChange={e => setGift(e.target.checked)} className="h-4 w-4 accent-ink" data-id="element-486" data-mp="3gxerdx01a1dm" />
                  <GiftIcon className="h-4 w-4 text-gold-deep" data-id="element-487" data-mp="3un03qojbvico" />
                  Dit is een cadeau
                </label>
                <AnimatePresence initial={false} data-id="element-488" data-mp="3qosvki2xjpd3">
                  {gift && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: "auto",
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2,
                ease
              }} className="overflow-hidden" data-id="element-489" data-mp="8hl6l5yo63qr">
                      <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3} maxLength={200} placeholder="Persoonlijk bericht (optioneel)" className="mt-2 w-full resize-none rounded-lg bg-paper px-4 py-3 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-ink" data-id="element-490" data-mp="n4jvoua20m2b" />
                    </motion.div>}
                </AnimatePresence>

                <button type="button" onClick={checkout} disabled={status === "loading"} className={`${btnPrimary} mt-6 h-14 w-full`} data-id="element-491" data-mp="3ql0rov0zl2jy">
                  {status === "loading" ? <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-492" data-mp="3vhhhrurqxqzo" /> : <LockIcon className="h-4 w-4" data-id="element-493" data-mp="3hjkigqlwr6hb" />}
                  {status === "loading" ? "Een moment…" : "Veilig afrekenen"}
                </button>
                <p className="mt-3 text-center text-xs text-muted" data-id="element-494" data-mp="3ln8q19aji22k">Via de beveiligde Shopify checkout</p>
              </div>
            </aside>
          </div>}
      </section>

      {suggestions.length > 0 && <section aria-labelledby="complete-heading" className="border-t border-line py-16 lg:py-24" data-id="element-495" data-mp="1icm813xlnsvh">
          <div className={container} data-id="element-496" data-mp="1de2lp2f3gf2l">
            <SectionHeader id="complete-heading" title="Maak het compleet" link={{
          label: "Bekijk alles",
          to: "/shop"
        }} data-id="element-497" data-mp="1df3uf08n0ph5" />
            <div className="mt-10" data-id="element-498" data-mp="2b0rq1ix9r3rm">
              <ProductGrid products={suggestions} data-id="element-499" data-mp="262q0qg99287p" />
            </div>
          </div>
        </section>}
    </>;
}